<template>
  <div class="document-viewer">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>正在加载文档，请稍候...</p>
    </div>

    <!-- 错误提示 -->
    <div v-else-if="error" class="error-container">
      <el-empty description="文档加载失败" />
      <p class="error-message">{{ errorMessage }}</p>
      <div class="error-actions">
        <el-button @click="openInNewTab" type="primary">在新标签页打开</el-button>
        <el-button @click="$emit('download')" type="default">下载文档</el-button>
      </div>
    </div>

    <!-- 文档预览区域 -->
    <div v-else class="preview-container">
      <!-- PDF预览：优先 object，失败时 fallback 内用 iframe 再试（应对线上 CSP/代理 导致 object 被拦） -->
      <div v-if="isPDF" class="pdf-preview">
        <object
          :data="previewUrl"
          type="application/pdf"
          class="document-frame"
          @load="handleFrameLoad"
        >
          <!-- object 无法加载时（如线上 Content-Disposition/CSP 导致）尝试 iframe -->
          <div class="pdf-fallback">
            <iframe
              v-if="previewUrl"
              :src="previewUrl"
              type="application/pdf"
              class="document-frame pdf-fallback-iframe"
              title="PDF 预览"
            />
            <p v-else class="fallback-tip">无法加载 PDF 预览</p>
            <div class="fallback-actions">
              <el-button @click="openInNewTab" type="primary" icon="el-icon-view">
                在新标签页打开
              </el-button>
              <el-button @click="$emit('download')" type="default" icon="el-icon-download">
                下载文档
              </el-button>
            </div>
          </div>
        </object>
      </div>

      <!-- PPTX 在线预览 -->
      <div v-else-if="isPPTX" class="pptx-preview">
        <PPTXPreviewer :src="src" :file-name="fileName || 'presentation.pptx'" />
      </div>

      <!-- 旧版 PPT / 其他 Office：提示下载 -->
      <div v-else-if="isOfficeDocument" class="office-preview">
        <el-empty :description="isLegacyPPT ? '暂不支持旧版 .ppt 在线预览' : '暂不支持此格式的在线预览'" />
        <div v-if="fallbackMessage" class="fallback-message">
          {{ fallbackMessage }}
        </div>
      </div>

      <!-- 不支持的文档格式 -->
      <div v-else class="unsupported-format">
        <el-empty description="不支持的文档格式" />
      </div>
    </div>

    <!-- 控制栏 -->
    <div class="controls">
      <el-button @click="$emit('download')" type="primary" icon="el-icon-download" class="download-btn">
        下载文档
      </el-button>
      <el-tooltip :content="currentFormat === 'pdf' ? 'PDF文档' : 'Office文档'" placement="top">
        <span class="format-tag">{{ currentFormat.toUpperCase() }}</span>
      </el-tooltip>
    </div>
  </div>
</template>

<script>
import PPTXPreviewer from './PPTXPreviewer.vue'

export default {
  name: 'EnhancedDocumentViewer',
  components: { PPTXPreviewer },
  props: {
    src: {
      type: String,
      required: true
    },
    fileName: {
      type: String,
      default: ''
    },
    fileFormat: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      error: false,
      errorMessage: '',
      fallbackMessage: '',
      loadTimeout: null
    }
  },
  watch: {
    src: {
      immediate: true,
      handler() {
        this.initializePreview()
      }
    },
    fileFormat() {
      this.initializePreview()
    }
  },
  computed: {
    // 判断文件类型
    fileExtension() {
      if (this.fileFormat) {
        return this.fileFormat.toLowerCase()
      }
      const extension = this.fileName && this.fileName.includes('.')
        ? this.fileName.split('.').pop().toLowerCase()
        : ''
      return extension || this.getExtensionFromUrl()
    },
    
    isPDF() {
      return this.fileExtension === 'pdf'
    },

    isPPTX() {
      return this.fileExtension === 'pptx'
    },

    isLegacyPPT() {
      return this.fileExtension === 'ppt'
    },
    
    isOfficeDocument() {
      const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp']
      return officeExtensions.includes(this.fileExtension) && !this.isPPTX
    },
    
    currentFormat() {
      if (this.isPDF) return 'pdf'
      if (this.isPPTX) return 'pptx'
      if (this.isOfficeDocument) return this.fileExtension || 'office'
      return 'unknown'
    },
    
    // 生成预览URL
    previewUrl() {
      if (this.isPDF) {
        console.log('PDF Preview URL:', this.src)
        return this.src // 直接使用PDF URL
      }
      return ''
    }
  },
  methods: {
    initializePreview() {
      // 清除之前的超时定时器
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout)
        this.loadTimeout = null
      }

      this.error = false
      this.errorMessage = ''
      this.fallbackMessage = ''
      
      if (this.isPPTX) {
        this.loading = false
        this.fallbackMessage = ''
        return
      }

      if (this.isLegacyPPT) {
        this.loading = false
        this.fallbackMessage = '旧版 .ppt 暂不支持在线预览，请另存为 .pptx 后重新上传，或下载后用 WPS/Office 打开。'
        return
      }

      if (this.isOfficeDocument) {
        this.loading = false
        this.fallbackMessage = '暂不支持此格式的在线预览，请下载后查看。'
        return
      }
      
      if (this.isPDF && this.previewUrl) {
        this.loading = true
        // 设置5秒超时，如果超时则自动结束加载（PDF通常会立即加载或失败）
        this.loadTimeout = setTimeout(() => {
          if (this.loading) {
            this.loading = false
            console.log('PDF preview initialized:', this.previewUrl)
          }
        }, 5000) // 5秒后自动结束加载状态
      } else {
        this.loading = false
      }
    },

    // 从URL获取文件扩展名
    getExtensionFromUrl() {
      // 尝试从URL中提取文件名和扩展名
      const urlParts = this.src.split('/')
      const fileName = urlParts[urlParts.length - 1]
      return fileName.split('.').pop()?.toLowerCase() || ''
    },
    
    // 处理iframe加载完成
    handleFrameLoad() {
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout)
        this.loadTimeout = null
      }
      this.loading = false
      this.error = false
      console.log('PDF loaded successfully:', this.previewUrl)
    },
    
    // 处理iframe加载错误
    handleFrameError(event) {
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout)
        this.loadTimeout = null
      }
      this.loading = false
      this.error = true
      this.errorMessage = '文档加载失败，请尝试在新标签页打开或下载后查看'
      console.error('PDF load error:', event, this.previewUrl)
    },
    
    // 在新标签页打开文档
    openInNewTab() {
      if (this.previewUrl) {
        window.open(this.previewUrl, '_blank')
      }
    }
  },
  
  beforeUnmount() {
    // 组件销毁时清除定时器
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout)
    }
  }
}
</script>

<style scoped>
.document-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(24, 144, 255, 0.2);
  border-radius: 50%;
  border-top-color: #1890ff;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  color: #f56c6c;
  margin: 16px 0;
  text-align: center;
}

.error-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.preview-container {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.pdf-preview {
  width: 100%;
  height: 100%;
  min-height: 600px;
}

.pptx-preview {
  width: 100%;
  min-height: 420px;
  text-align: left;
  padding: 8px;
}

.document-frame {
  width: 100%;
  height: 100%;
  min-height: 600px;
  transition: opacity 0.3s;
}

.pdf-fallback {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  height: 100%;
  min-height: 600px;
  padding: 16px;
  background: #f5f5f5;
}

.pdf-fallback-iframe {
  flex: 1;
  min-height: 400px;
  border: none;
  margin-bottom: 12px;
}

.fallback-tip {
  color: #909399;
  margin: 16px 0 8px;
  text-align: center;
}

.fallback-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  justify-content: center;
  flex-shrink: 0;
}

.fallback-message {
  text-align: center;
  color: #909399;
  font-size: 14px;
  margin-top: 16px;
  padding: 0 20px;
}

.office-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
  text-align: center;
}

.unsupported-format {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
}

.controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #fff;
  border-top: 1px solid #e6e6e6;
}

.download-btn {
  margin-right: 16px;
}

.format-tag {
  background: #f0f0f0;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  color: #606266;
}
</style>